import { LOGO_URL, COMPANY } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, BadgePercent, Leaf, Phone, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function LandingPage() {
  return (
    <div data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1557844352-761f2565b576?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxmcmVzaCUyMHZlZ2V0YWJsZXMlMjBtYXJrZXR8ZW58MHx8fHwxNzgwNDkxMDU3fDA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A2B1E]/85 via-[#1A2B1E]/55 to-transparent" />
          <div className="ff-grain" />
        </div>
        <div className="relative ff-container py-32 md:py-40 grid md:grid-cols-12 gap-8 items-center min-h-[80vh]">
          <div className="md:col-span-7 ff-fade-in text-white">
            <p className="ff-overline text-[#F4A261]">Primeur Pro · Genève</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl mt-5 leading-[1.05] tracking-tight">
              Le meilleur du <em className="not-italic text-[#F4A261]">marché</em>,<br />
              livré chaque matin.
            </h1>
            <p className="mt-7 text-lg text-[#E5E3DC] max-w-xl leading-relaxed">
              Fournisseur B2B de fruits & légumes frais pour les restaurateurs et commerçants
              du bassin genevois. Sélection rigoureuse, prix justes, livraison rapide.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-register-cta" className="ff-btn-accent">
                Ouvrir un compte pro <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="hero-login-cta" className="bg-white/10 border border-white/30 text-white px-8 py-[14px] hover:bg-white hover:text-[#1A2B1E] transition-colors font-medium inline-flex items-center gap-2">
                J'ai déjà un compte
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-6 text-sm text-[#C9D2CC]">
              <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-2 hover:text-white">
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Le Lignon, Genève
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ARGUMENTS */}
      <section className="py-24 ff-container" data-testid="arguments-section">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="ff-overline">Pourquoi nous choisir</p>
            <h2 className="font-serif text-4xl lg:text-5xl mt-4 tracking-tight leading-tight text-[#1A2B1E]">
              Trois engagements,<br />zéro compromis.
            </h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-6 ff-stagger">
            <div className="ff-card p-8" data-testid="arg-price">
              <BadgePercent className="w-8 h-8 text-[#E76F51] stroke-[1.5]" />
              <h3 className="font-serif text-2xl mt-6">Prix justes</h3>
              <p className="text-sm text-[#4A5D4E] mt-3 leading-relaxed">
                Tarifs négociés au plus près des producteurs. Prix dégressifs sur volume.
              </p>
            </div>
            <div className="ff-card p-8" data-testid="arg-reliability">
              <ShieldCheck className="w-8 h-8 text-[#2D6A4F] stroke-[1.5]" />
              <h3 className="font-serif text-2xl mt-6">Fiabilité</h3>
              <p className="text-sm text-[#4A5D4E] mt-3 leading-relaxed">
                Sélection à la main, contrôle qualité à chaque palette. Engagement de fraîcheur.
              </p>
            </div>
            <div className="ff-card p-8" data-testid="arg-speed">
              <Truck className="w-8 h-8 text-[#2D6A4F] stroke-[1.5]" />
              <h3 className="font-serif text-2xl mt-6">Rapidité</h3>
              <p className="text-sm text-[#4A5D4E] mt-3 leading-relaxed">
                Commande avant 22h, livrée le matin même sur Genève et environs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGERY */}
      <section className="relative py-32 bg-[#F2EFEB]" data-testid="story-section">
        <div className="ff-container grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1572715376701-98568319fd0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxjaGVmJTIwY29va2luZyUyMHJlc3RhdXJhbnR8ZW58MHx8fHwxNzgwNDkxMDU3fDA&ixlib=rb-4.1.0&q=85"
                alt="Chef sélectionnant des légumes"
                className="w-full h-[520px] object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="ff-overline">Pour les pros, par des pros</p>
            <h2 className="font-serif text-4xl lg:text-5xl mt-4 tracking-tight leading-tight">
              Conçu pour la cadence<br />des cuisines exigeantes.
            </h2>
            <p className="mt-6 text-[#4A5D4E] leading-relaxed text-lg">
              Restaurateurs, traiteurs, épiceries fines, hôtels — FineFood Primeur centralise
              vos commandes dans un espace dédié. Catalogue mis à jour quotidiennement,
              facturation pro, livraison sur deux adresses possibles.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-[#1A2B1E]">
              <li className="flex items-center gap-3"><Leaf className="w-4 h-4 text-[#2D6A4F]" /> Catalogue de saison mis à jour quotidiennement</li>
              <li className="flex items-center gap-3"><Leaf className="w-4 h-4 text-[#2D6A4F]" /> Facturation B2B et reçus PDF automatiques</li>
              <li className="flex items-center gap-3"><Leaf className="w-4 h-4 text-[#2D6A4F]" /> Livraison flexible : siège ou point de service</li>
            </ul>
            <Link to="/register" data-testid="story-cta" className="ff-btn-primary mt-10">
              Demander mon accès <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CONTACT BAND */}
      <section className="bg-[#2D6A4F] text-white py-20" data-testid="contact-band">
        <div className="ff-container grid md:grid-cols-3 gap-10">
          <div>
            <p className="ff-overline text-white">Notre adresse</p>
            <p className="mt-4 font-serif text-2xl leading-snug">
              {COMPANY.address}<br />
              {COMPANY.postal} {COMPANY.city}
            </p>
          </div>
          <div>
            <p className="ff-overline text-white">Téléphone</p>
            <a href={`tel:${COMPANY.phone}`} className="mt-4 font-serif text-2xl block hover:text-[#F4A261]">
              {COMPANY.phone}
            </a>
          </div>
          <div>
            <p className="ff-overline text-white">Commandes</p>
            <p className="mt-4 text-sm text-[#C9D2CC]">
              Du lundi au samedi, avant 22h pour livraison le matin suivant.
            </p>
            <Link to="/login" className="mt-4 inline-flex items-center gap-2 text-[#F4A261] hover:underline" data-testid="contact-band-login">
              Accéder à mon espace <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
